import React from 'react';
import { Stack, TextField, MenuItem, Alert, Button, Typography, Box } from '@mui/material';
import { useCalendarPreferences } from './calendarPreferences';
import { formatDisplayDateTime } from './useDateTimeDisplay';
import { formatClockText, clockForTimezone } from './clockSystem';
import TimezoneSelector from './timezoneSelector';
import { timezoneDisplayName } from './timezoneOptions';
export function DateTimePreferenceFields({ prefs, timezone, onUseEthiopia, savingTimezone, timezoneError, onAdjustTimezone, onTimezoneSelect }) {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);
  const localTime = zone => new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(now);
  const effectiveTimezone = timezone || prefs.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const displayClock = clockForTimezone(prefs.displayClock, effectiveTimezone);
  const ethiopianTimezone = effectiveTimezone === 'Africa/Addis_Ababa';
  if (!prefs.ethiopianEnabled) return null;
  return <Stack spacing={2} sx={{ my: 2 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField disabled={prefs.savingPreference} select fullWidth size="small" label="Calendar" value={prefs.displayCalendar} onChange={e => prefs.setDisplayCalendar(e.target.value)}>
        <MenuItem value="gregory">Gregorian</MenuItem><MenuItem value="ethiopic">Ethiopian</MenuItem>
      </TextField>
      <TextField disabled={prefs.savingPreference} select fullWidth size="small" label="Time format" helperText={!ethiopianTimezone ? "Day/Night requires Addis Ababa timezone." : undefined} value={displayClock} onChange={e => prefs.setDisplayClock(e.target.value)}>
        <MenuItem value="standard">Standard — 9:00 AM</MenuItem><MenuItem value="ethiopian" disabled={!ethiopianTimezone}>Ethiopian only — 3:00 daytime</MenuItem><MenuItem value="both" disabled={!ethiopianTimezone}>Both — 3:00 daytime (9:00 AM)</MenuItem>
      </TextField>
    </Stack>
    {onTimezoneSelect ? (
      <TimezoneSelector timezoneId={effectiveTimezone} onTimezoneSelect={onTimezoneSelect} />
    ) : onAdjustTimezone ? (
      <Button onClick={onAdjustTimezone} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>Change timezone</Button>
    ) : null}
    {prefs.displayCalendar === 'ethiopic' && effectiveTimezone !== 'Africa/Addis_Ababa' && (
      <Alert severity="info" sx={{ '& .MuiAlert-message': { py: 0 } }}>
        <Typography variant="body2">Using the Ethiopian calendar? Switch to Ethiopia time?</Typography>
        <Typography variant="caption" display="block" color="text.secondary">Current timezone: {timezoneDisplayName(effectiveTimezone)} ? {localTime(effectiveTimezone)}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>Addis Ababa now: {formatClockText(localTime('Africa/Addis_Ababa'), 'both')}</Typography>
        {(onUseEthiopia || onTimezoneSelect) && (
          <Button size="small" disabled={savingTimezone || prefs.savingPreference}
            onClick={() => {
              prefs.setDisplayClock(prefs.displayClock === 'both' ? 'both' : 'ethiopian');
              if (onUseEthiopia) onUseEthiopia();
              else onTimezoneSelect('Africa/Addis_Ababa');
            }}
            sx={{ textTransform: 'none', p: 0, mt: 0.5 }}>
            {savingTimezone ? 'Saving...' : 'Use Ethiopia time'}
          </Button>
        )}
        {!onUseEthiopia && !onTimezoneSelect && <Typography variant="body2">Choose Addis Ababa in your timezone settings.</Typography>}
      </Alert>
    )}
    {onUseEthiopia && prefs.displayCalendar === 'ethiopic' && effectiveTimezone === 'Africa/Addis_Ababa' && <Typography variant="body2" color="text.secondary">Using Addis Ababa time (UTC+03:00).</Typography>}
    {timezoneError && <Alert severity="error">{timezoneError}</Alert>}
    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
      <Typography variant="overline">Preview</Typography>
      <Typography variant="body2">{formatDisplayDateTime(now, prefs.displayCalendar, displayClock, effectiveTimezone)}</Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">Changes apply automatically.</Typography>
    {prefs.preferenceError && <Alert severity="warning">{prefs.preferenceError}</Alert>}
  </Stack>;
}
export default function DateTimePreferences({ timezone, onUseEthiopia, savingTimezone, timezoneError, onAdjustTimezone } = {}) {
  const prefs = useCalendarPreferences();
  return <DateTimePreferenceFields prefs={prefs} timezone={timezone} onUseEthiopia={onUseEthiopia} savingTimezone={savingTimezone} timezoneError={timezoneError} onAdjustTimezone={onAdjustTimezone} />;
}
